/* eslint-disable react-refresh/only-export-components */
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

/**
 * Legacy status pill kept for TierBadge and older call sites.
 * New code should use `Badge` from `components/ui`; the tones here match it.
 */
const badgeVariants = cva(
  "inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full font-medium ring-1 ring-inset",
  {
    variants: {
      variant: {
        default: "bg-gray-50 text-gray-700 ring-gray-200",
        primary: "bg-brand-50 text-brand-700 ring-brand-200",
        secondary: "bg-gray-100 text-gray-700 ring-gray-200",
        success: "bg-emerald-50 text-emerald-700 ring-emerald-200",
        warning: "bg-amber-50 text-amber-800 ring-amber-200",
        danger: "bg-red-50 text-red-700 ring-red-200",
        info: "bg-sky-50 text-sky-700 ring-sky-200",
        /** Unverified — quiet neutral */
        tier1: "bg-gray-50 text-gray-600 ring-gray-200",
        /** Verified — trust blue */
        tier2: "bg-sky-50 text-sky-700 ring-sky-200",
        /** Pro — restrained violet */
        tier3: "bg-violet-50 text-violet-700 ring-violet-200",
        /** Certified Pro — muted gold */
        tier4: "bg-amber-50 text-amber-800 ring-amber-300",
      },
      size: {
        small: "h-5 gap-1 px-2 text-xs",
        medium: "h-6 gap-1.5 px-2.5 text-xs",
        large: "h-7 gap-1.5 px-3 text-meta",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "medium",
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  variant?:
    | "default"
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "danger"
    | "info"
    | "tier1"
    | "tier2"
    | "tier3"
    | "tier4";
  size?: "small" | "medium" | "large";
  label?: string;
  icon?: React.ReactNode;
  showTooltip?: boolean;
  tooltipText?: string;
}

function Badge({
  variant = "default",
  size = "medium",
  label,
  icon,
  showTooltip = false,
  tooltipText,
  className,
  children,
  ...props
}: BadgeProps) {
  const content = children || label;

  return (
    <div
      className={cn(badgeVariants({ variant, size }), className)}
      title={showTooltip && tooltipText ? tooltipText : undefined}
      role="status"
      aria-label={typeof content === "string" ? content : undefined}
      {...props}
    >
      {icon && (
        <span aria-hidden className="flex shrink-0 items-center">
          {icon}
        </span>
      )}
      {content && <span className="whitespace-nowrap">{content}</span>}
    </div>
  );
}

export { Badge, badgeVariants };
